import { useCallback, useSyncExternalStore } from 'react';

// Favourites: bus stops, shops, train stations, fuel stations. One store for the whole app.
//
// There used to be two writers of this key with different shapes: this hook stored
// { type, id, name } (Torrebus, Comercio) and FavoriteButton stored { type, item_id, label }
// (Renfe, Gasolineras), each with its own copy of the state, so a star set on one screen did not
// show on another until a reload. Now every reader and writer goes through this module: items are
// normalised on read (either shape is understood, so nobody's saved favourites are lost), written
// with both key names for any old code still reading them, and every subscriber re-renders on a
// change, in this tab or another.
const STORAGE_KEY = 'torre_favorites';
const listeners = new Set();

function normalise(f) {
  if (!f || !f.type) return null;
  const id = f.id ?? f.item_id;
  if (id === undefined || id === null || id === '') return null;
  const name = f.name ?? f.label ?? '';
  return { ...f, id: String(id), item_id: String(id), name, label: name };
}

let cache = null;
function read() {
  if (cache) return cache;
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    const seen = new Set();
    cache = raw.map(normalise).filter((f) => {
      if (!f) return false;
      const k = `${f.type}|${f.id}`;
      if (seen.has(k)) return false;
      seen.add(k); return true;
    });
  } catch { cache = []; }
  return cache;
}

function write(items) {
  cache = items;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* storage unavailable */ }
  listeners.forEach((l) => l());
}

function subscribe(listener) {
  listeners.add(listener);
  const onStorage = (e) => { if (e.key === STORAGE_KEY) { cache = null; listener(); } };
  window.addEventListener('storage', onStorage);
  return () => { listeners.delete(listener); window.removeEventListener('storage', onStorage); };
}

/** Put favourites first, keeping the list's own order within each group (sort is stable). */
export function favouritesFirst(list, isFav) {
  return [...list].sort((a, b) => (isFav(b) ? 1 : 0) - (isFav(a) ? 1 : 0));
}

export function useFavorites() {
  const favorites = useSyncExternalStore(subscribe, read, () => []);

  const isFavorite = useCallback((type, id) => {
    const key = String(id);
    return favorites.some((f) => f.type === type && f.id === key);
  }, [favorites]);

  const toggleFavorite = useCallback((item) => {
    const n = normalise(item);
    if (!n) return;
    const current = read();
    const exists = current.some((f) => f.type === n.type && f.id === n.id);
    write(exists ? current.filter((f) => !(f.type === n.type && f.id === n.id)) : [...current, n]);
  }, []);

  const getFavoritesByType = useCallback((type) => favorites.filter((f) => f.type === type), [favorites]);

  return { favorites, isFavorite, toggleFavorite, getFavoritesByType };
}
